import type { ReactNode } from "react";

export type HomeExploreActionId = "open_trips" | "plan_timing" | "search_explore" | "manage_offline" | "open_saved" | "sign_in";
export type HomeExploreCapabilityId = "explore_search" | "deals" | "trip_sync" | "offline_essentials" | "planner";

export interface HomeExploreTrip {
  readonly tripId: string;
  readonly name: string;
  readonly destination: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timeZone: string;
  readonly savedOffline: boolean;
  readonly offlineItemCount: number;
}

export interface HomeExploreSavedCounts {
  readonly places: number;
  readonly offers: number;
  readonly events: number;
  readonly livestreams: number;
  readonly offlineTrips: number;
  readonly offlineTickets: number;
  readonly offlineNotes: number;
}

export interface HomeExplorePointsState {
  readonly balance: number | null;
  readonly source: "server" | "unavailable" | "signed_out";
  readonly reason: string;
}

export interface HomeExploreCapabilityInput {
  readonly id: HomeExploreCapabilityId;
  readonly label: string;
  readonly actionable: boolean;
  readonly reason: string;
  readonly providers: readonly string[];
}

export interface HomeExploreCapabilityState extends HomeExploreCapabilityInput {
  readonly status: "available" | "unavailable";
  readonly helperText: string;
  readonly fakeInventoryVisible: false;
}

export interface HomeExploreTripSummary {
  readonly status: "current" | "next" | "none";
  readonly headline: string;
  readonly detail: string;
  readonly dateRange: string | null;
  readonly offlineReady: boolean;
  readonly offlineDetail: string;
  readonly tripId: string | null;
}

export interface HomeExploreCountsSummary {
  readonly savedTotal: number;
  readonly offlineTotal: number;
  readonly savedDetail: string;
  readonly offlineDetail: string;
  readonly hasAnySaved: boolean;
  readonly hasOfflineEssentials: boolean;
}

export interface HomeExploreQuickAction {
  readonly id: HomeExploreActionId;
  readonly label: string;
  readonly enabled: boolean;
  readonly reason: string;
}

export interface HomeExploreHelperModel {
  readonly tripSummary: HomeExploreTripSummary;
  readonly counts: HomeExploreCountsSummary;
  readonly points: HomeExplorePointsState;
  readonly liveState: "available" | "limited";
  readonly liveDetail: string;
  readonly capabilities: readonly HomeExploreCapabilityState[];
  readonly quickActions: readonly HomeExploreQuickAction[];
  readonly noFakeInventory: true;
}

export interface HomeExploreHelperPanelProps {
  readonly trips: readonly HomeExploreTrip[];
  readonly savedCounts: HomeExploreSavedCounts;
  readonly points?: HomeExplorePointsState;
  readonly capabilities: readonly HomeExploreCapabilityInput[];
  readonly now: string;
  readonly signedIn: boolean;
  readonly liveServicesAvailable: boolean;
  readonly onAction?: (action: HomeExploreActionId) => void;
}

function parseCanonicalInstant(value: string, field: string): number {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) {
    throw new Error(`${field} must be a canonical ISO timestamp`);
  }
  return parsed;
}

function assertCount(value: number, field: string): number {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${field} must be a non-negative safe integer`);
  return value;
}

function assertTrip(trip: HomeExploreTrip): HomeExploreTrip {
  if (!trip.tripId.trim() || !trip.name.trim() || !trip.destination.trim()) {
    throw new Error("Trip summary requires a trip id, name, and destination");
  }
  if (!Intl.supportedValuesOf("timeZone").includes(trip.timeZone)) {
    throw new Error(`Unsupported IANA time zone: ${trip.timeZone}`);
  }
  const startsAt = parseCanonicalInstant(trip.startsAt, "Trip start");
  const endsAt = parseCanonicalInstant(trip.endsAt, "Trip end");
  if (endsAt < startsAt) throw new Error("Trip end must not precede trip start");
  assertCount(trip.offlineItemCount, "Offline item count");
  return trip;
}

function shortDate(value: string): string {
  return value.slice(0, 10);
}

export function createCapabilityState(input: HomeExploreCapabilityInput): HomeExploreCapabilityState {
  if (!input.id.trim() || !input.label.trim() || !input.reason.trim()) {
    throw new Error("Capability states require id, label, and reason");
  }
  const providers = Object.freeze(input.providers.map((provider) => provider.trim()).filter(Boolean));
  const available = input.actionable && providers.length > 0;
  const unavailableCopy: Readonly<Record<HomeExploreCapabilityId, string>> = Object.freeze({
    explore_search: "Search will appear here when an approved travel source is ready for this request.",
    deals: "Deals will appear after a verified offer source is active.",
    trip_sync: "Trip sync is waiting for live account services.",
    offline_essentials: "Offline essentials are stored on this device.",
    planner: "Timing checks will unlock when the planner service is connected."
  });
  return Object.freeze({
    ...input,
    providers,
    status: available ? "available" as const : "unavailable" as const,
    helperText: available
      ? `${input.label} is ready.`
      : unavailableCopy[input.id],
    fakeInventoryVisible: false as const
  });
}

export function createTripSummary(trips: readonly HomeExploreTrip[], now: string): HomeExploreTripSummary {
  const currentTime = parseCanonicalInstant(now, "Current time");
  const candidates = trips.map(assertTrip)
    .filter((trip) => currentTime <= Date.parse(trip.endsAt))
    .sort((left, right) => {
      const leftCurrent = currentTime >= Date.parse(left.startsAt) && currentTime <= Date.parse(left.endsAt);
      const rightCurrent = currentTime >= Date.parse(right.startsAt) && currentTime <= Date.parse(right.endsAt);
      if (leftCurrent !== rightCurrent) return leftCurrent ? -1 : 1;
      return Date.parse(left.startsAt) - Date.parse(right.startsAt) || left.tripId.localeCompare(right.tripId);
    });
  const trip = candidates[0];
  if (trip === undefined) {
    return Object.freeze({
      status: "none" as const,
      headline: "No current trip yet",
      detail: "Create a trip to keep plans, saved places, notes, and timing checks together.",
      dateRange: null,
      offlineReady: false,
      offlineDetail: "No offline trip essentials are saved.",
      tripId: null
    });
  }
  const startsAt = Date.parse(trip.startsAt);
  const status = currentTime >= startsAt ? "current" as const : "next" as const;
  return Object.freeze({
    status,
    headline: `${status === "current" ? "Current" : "Next"} trip: ${trip.name}`,
    detail: `${trip.destination} (${trip.timeZone})`,
    dateRange: `${shortDate(trip.startsAt)} to ${shortDate(trip.endsAt)}`,
    offlineReady: trip.savedOffline,
    offlineDetail: trip.savedOffline
      ? `${trip.offlineItemCount} offline essentials saved for this trip.`
      : "Save this trip offline before travel if tickets, notes, or essentials need airplane-mode access.",
    tripId: trip.tripId
  });
}

export function createCountsSummary(input: HomeExploreSavedCounts): HomeExploreCountsSummary {
  const savedTotal =
    assertCount(input.places, "Saved places") +
    assertCount(input.offers, "Saved offers") +
    assertCount(input.events, "Saved events") +
    assertCount(input.livestreams, "Saved livestreams");
  const offlineTotal =
    assertCount(input.offlineTrips, "Offline trips") +
    assertCount(input.offlineTickets, "Offline tickets") +
    assertCount(input.offlineNotes, "Offline notes");
  return Object.freeze({
    savedTotal,
    offlineTotal,
    savedDetail: `${input.places} places, ${input.offers} offers, ${input.events} events, ${input.livestreams} livestreams saved.`,
    offlineDetail: `${input.offlineTrips} trips, ${input.offlineTickets} tickets, ${input.offlineNotes} notes available offline.`,
    hasAnySaved: savedTotal > 0,
    hasOfflineEssentials: offlineTotal > 0
  });
}

export function createQuickActions(input: {
  readonly signedIn: boolean;
  readonly liveServicesAvailable: boolean;
  readonly tripSummary: HomeExploreTripSummary;
  readonly counts: HomeExploreCountsSummary;
  readonly capabilities: readonly HomeExploreCapabilityState[];
}): readonly HomeExploreQuickAction[] {
  const capability = (id: HomeExploreCapabilityId) => input.capabilities.find((candidate) => candidate.id === id);
  const explore = capability("explore_search");
  const planner = capability("planner");
  const tripSync = capability("trip_sync");
  return Object.freeze([
    Object.freeze({
      id: "search_explore" as const,
      label: "Search Explore",
      enabled: explore?.status === "available",
      reason: explore?.status === "available" ? "Search is ready for this request." : explore?.helperText ?? "Explore search is not ready yet."
    }),
    Object.freeze({
      id: "plan_timing" as const,
      label: "Check timing",
      enabled: input.liveServicesAvailable && planner?.status === "available",
      reason: input.liveServicesAvailable && planner?.status === "available" ? "Timing checks can run now." : "Timing checks will appear when the planner is ready."
    }),
    Object.freeze({
      id: "open_trips" as const,
      label: input.tripSummary.tripId === null ? "Create trip" : "Open trip",
      enabled: input.liveServicesAvailable && tripSync?.status === "available",
      reason: input.liveServicesAvailable && tripSync?.status === "available" ? "Trip tools are ready." : "Trip creation and syncing will appear when live services are connected."
    }),
    Object.freeze({
      id: "manage_offline" as const,
      label: "Offline essentials",
      enabled: input.counts.hasOfflineEssentials,
      reason: input.counts.hasOfflineEssentials ? input.counts.offlineDetail : "No offline essentials are saved yet."
    }),
    Object.freeze({
      id: "open_saved" as const,
      label: "Saved items",
      enabled: input.counts.hasAnySaved,
      reason: input.counts.hasAnySaved ? input.counts.savedDetail : "No saved places, offers, events, or livestreams yet."
    }),
    Object.freeze({
      id: "sign_in" as const,
      label: "Sign in to sync",
      enabled: !input.signedIn,
      reason: input.signedIn ? "Signed in account sync is active." : "Sign in to sync trips, saves, points, and offline records across devices."
    })
  ]);
}

export function createPointsState(input: { readonly signedIn: boolean; readonly balance?: number | null; readonly reason?: string }): HomeExplorePointsState {
  if (!input.signedIn) {
    return Object.freeze({
      balance: null,
      source: "signed_out" as const,
      reason: input.reason ?? "Sign in to see your points."
    });
  }
  if (input.balance === undefined || input.balance === null) {
    return Object.freeze({
      balance: null,
      source: "unavailable" as const,
      reason: input.reason ?? "Points are unavailable right now."
    });
  }
  assertCount(input.balance, "Points balance");
  return Object.freeze({
    balance: input.balance,
    source: "server" as const,
    reason: input.reason ?? "Loaded from your rewards account."
  });
}

export function createHomeExploreHelperModel(input: {
  readonly trips: readonly HomeExploreTrip[];
  readonly savedCounts: HomeExploreSavedCounts;
  readonly points?: HomeExplorePointsState;
  readonly capabilities: readonly HomeExploreCapabilityInput[];
  readonly now: string;
  readonly signedIn: boolean;
  readonly liveServicesAvailable: boolean;
}): HomeExploreHelperModel {
  const tripSummary = createTripSummary(input.trips, input.now);
  const counts = createCountsSummary(input.savedCounts);
  const capabilities = Object.freeze(input.capabilities.map(createCapabilityState));
  return Object.freeze({
    tripSummary,
    counts,
    points: input.points ?? createPointsState({ signedIn: input.signedIn }),
    liveState: input.liveServicesAvailable ? "available" as const : "limited" as const,
    liveDetail: input.liveServicesAvailable
      ? "Search, trips, account sync, and timing checks are available when their sources are ready."
      : "Saved items and offline essentials remain visible while live actions reconnect.",
    capabilities,
    quickActions: createQuickActions({
      signedIn: input.signedIn,
      liveServicesAvailable: input.liveServicesAvailable,
      tripSummary,
      counts,
      capabilities
    }),
    noFakeInventory: true as const
  });
}

function HelperCard(props: { readonly title: string; readonly eyebrow: string; readonly children: ReactNode }) {
  return <article className="card">
    <span className="eyebrow">{props.eyebrow}</span>
    <h3>{props.title}</h3>
    {props.children}
  </article>;
}

export function HomeExploreHelperPanel(props: HomeExploreHelperPanelProps) {
  const model = createHomeExploreHelperModel(props);
  return <section className="section" aria-labelledby="home-explore-helper-title">
    <div className="section-heading">
      <div>
        <span className="eyebrow">HOME</span>
        <h2 id="home-explore-helper-title">Your next useful move</h2>
      </div>
      <p>Start with the next travel action, not system status.</p>
    </div>
    <div className="grid">
      <HelperCard eyebrow="TRIP" title={model.tripSummary.headline}>
        <p>{model.tripSummary.detail}</p>
        {model.tripSummary.dateRange && <p>{model.tripSummary.dateRange}</p>}
        <p>{model.tripSummary.offlineDetail}</p>
      </HelperCard>
      <HelperCard eyebrow="SAVED" title={`${model.counts.savedTotal} saved`}>
        <p>{model.counts.savedDetail}</p>
        <p>{model.counts.offlineDetail}</p>
      </HelperCard>
      <HelperCard eyebrow="POINTS" title={model.points.balance === null ? "Points unavailable" : `${model.points.balance.toLocaleString()} points`}>
        <p>{model.points.reason}</p>
      </HelperCard>
      <HelperCard eyebrow="LIVE" title={model.liveState === "available" ? "Ready when sources are available" : "Limited live mode"}>
        <p>{model.liveDetail}</p>
      </HelperCard>
    </div>
    <div className="button-row" aria-label="Contextual quick actions">
      {model.quickActions.map((action) =>
        <button key={action.id} type="button" className={action.enabled ? "secondary" : "ghost"} disabled={!action.enabled} title={action.reason} onClick={() => props.onAction?.(action.id)}>
          {action.label}
        </button>
      )}
    </div>
  </section>;
}
